import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';

/**
 * Add/remove ONE user to/from a group, from their manage page — the
 * user-centric counterpart to useUsersBulkMembershipLogic (which edits many
 * users of one group). Both mutations invalidate everything a membership
 * change touches (the user's groups, the group's members, effective scopes).
 */
export const useUserGroupsLogic = (tenantId: string | undefined, userId: string | undefined) => {
  const queryClient = useQueryClient();

  const invalidate = (groupId: string) => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.users(tenantId ?? '') });
    void queryClient.invalidateQueries({ queryKey: queryKeys.groups(tenantId ?? '') });
    void queryClient.invalidateQueries({ queryKey: queryKeys.group(tenantId ?? '', groupId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.user(tenantId ?? '', userId ?? '') });
    void queryClient.invalidateQueries({ queryKey: queryKeys.me() });
  };

  const addMutation = useMutation<void, Error, string>({
    mutationFn: async (groupId) => {
      await apiRequest<void>(`/api/groups/${groupId}/members`, {
        method: 'POST',
        body: { userId },
      });
    },
    onSuccess: (_v, groupId) => invalidate(groupId),
  });

  const removeMutation = useMutation<void, Error, string>({
    mutationFn: async (groupId) => {
      await apiRequest<void>(`/api/groups/${groupId}/members/${userId}`, {
        method: 'DELETE',
      });
    },
    onSuccess: (_v, groupId) => invalidate(groupId),
  });

  return {
    isSubmitting: addMutation.isPending || removeMutation.isPending,
    error: addMutation.error ?? removeMutation.error,
    addGroup: async (groupId: string) => addMutation.mutateAsync(groupId),
    removeGroup: async (groupId: string) => removeMutation.mutateAsync(groupId),
  };
};
